import { formatQuantityDisplay } from '@/composables/useQuantity'
import {
  formatUnitPriceCurrency,
  roundUnitPrice,
  UNIT_DECIMALS_DEFAULT,
  UNIT_DECIMALS_EXTENDED
} from '@/utils/moneyScale'
import { tabStatementActiveEntries } from '@/utils/thermalReceiptVolcoraTab'

const CHARGE_ENTRY_TYPES = new Set(['charge', 'adjustment'])

function entryAllowFractional (entry) {
  if (typeof entry?.allow_fractional === 'boolean') {
    return entry.allow_fractional
  }
  return null
}

function unitPriceDecimalsFromEntry (entry) {
  if (entry?.ring_by_amount) return UNIT_DECIMALS_EXTENDED
  const decimals = Number(entry?.price_decimals)
  return decimals === UNIT_DECIMALS_EXTENDED ? UNIT_DECIMALS_EXTENDED : UNIT_DECIMALS_DEFAULT
}

/**
 * Resolve goods unit price (ex-tax) for a tab entry.
 */
export function resolveTabEntryUnitPrice (entry) {
  const fromApi = Number(entry?.unit_price)
  if (Number.isFinite(fromApi) && fromApi >= 0) {
    return fromApi
  }
  const quantity = Number(entry?.quantity) || 1
  const amountMinor = Number(entry?.amount_minor || 0)
  if (quantity <= 0 || amountMinor <= 0) {
    return 0
  }
  return roundUnitPrice(amountMinor / 100 / quantity, unitPriceDecimalsFromEntry(entry))
}

export function formatTabEntryUnitPrice (entry) {
  return formatUnitPriceCurrency(resolveTabEntryUnitPrice(entry), unitPriceDecimalsFromEntry(entry))
}

export function formatTabEntryQuantity (entry) {
  return formatQuantityDisplay(entry?.quantity, { allowFractional: entryAllowFractional(entry) })
}

export function tabChargeEntriesForReceipt (entries) {
  return tabStatementActiveEntries(entries).filter((entry) =>
    CHARGE_ENTRY_TYPES.has(String(entry?.entry_type || 'charge'))
  )
}

/**
 * Map tab ledger charge entries into sale receipt line items.
 *
 * @param {object[]|null|undefined} entries
 * @param {{ tabId?: number|string, currency?: string }} [opts]
 * @returns {object[]}
 */
export function tabEntryToReceiptLineItems (entries, { tabId = null } = {}) {
  return tabChargeEntriesForReceipt(entries).map((entry, idx) => {
    const quantity = Number(entry.quantity) || 1
    const amountMinor = Number(entry.amount_minor || 0)
    const taxMinor = Number(entry.tax_minor || 0)
    const lineMinor = amountMinor + taxMinor
    const lineTotal = lineMinor / 100
    const unitPrice = resolveTabEntryUnitPrice(entry)
    const subtotal = amountMinor / 100
    const priceDecimals = unitPriceDecimalsFromEntry(entry)

    return {
      id: `tab-entry-${entry.id ?? idx}`,
      name: entry.description || 'Item',
      quantity,
      price: unitPrice,
      price_decimals: priceDecimals,
      total: lineTotal,
      subtotal,
      tax_amount: taxMinor / 100,
      discount_amount: 0,
      is_tab_entry: true,
      tab_id: tabId ?? entry.tab_id ?? null,
      allow_fractional: entryAllowFractional(entry),
      ring_by_amount: Boolean(entry?.ring_by_amount),
      product: {
        allow_fractional: entryAllowFractional(entry),
        ring_by_amount: Boolean(entry?.ring_by_amount),
        price_decimals: priceDecimals
      }
    }
  })
}

export function tabEntryReceiptLineItemsFromContext (context) {
  if (!context) return []
  return tabEntryToReceiptLineItems(context.entries, { tabId: context.tab_id })
}

const CHARGE_TYPES = new Set(['charge', 'adjustment'])

/**
 * Map register ledger entries (including voided) for the POS on-tab list.
 *
 * @param {object[]|null|undefined} entries
 * @param {{ isVoided?: (entry: object) => boolean }} [opts]
 */
export function tabRegisterLedgerDisplayLines (entries, { isVoided = null } = {}) {
  const rows = Array.isArray(entries) ? entries : []
  return rows
    .filter((entry) => CHARGE_TYPES.has(String(entry?.entry_type || 'charge')))
    .map((entry, idx) => {
      const quantity = Number(entry.quantity) || 1
      const amountMinor = Number(entry.amount_minor || 0)
      const taxMinor = Number(entry.tax_minor || 0)
      const lineMinor = amountMinor + taxMinor
      const lineTotal = lineMinor / 100
      const unitPrice = resolveTabEntryUnitPrice(entry)
      const voided = typeof isVoided === 'function'
        ? isVoided(entry)
        : Boolean(entry?.voided_at)

      return {
        id: `tab-entry-${entry.id ?? idx}`,
        entryId: entry.id,
        name: entry.description || 'Item',
        quantity,
        price: unitPrice,
        price_decimals: unitPriceDecimalsFromEntry(entry),
        total: lineTotal,
        voided,
        allow_fractional: entryAllowFractional(entry),
        ring_by_amount: Boolean(entry?.ring_by_amount)
      }
    })
}
